import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AlgoliaFinancialInstitutionBranch } from '@fnModels/algolia/financial_institutions';
import {
	Branch,
	FinancialInstitutionLender,
	MortgageeType,
} from '@models/lender';
import { MatterFileType } from '@models/matter';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { InstitutionBranchRepo } from '@repos/institutionBranch/institution-branch.service';
import { FinancialInstitutionBranchSearchState } from '@state/algolia-search/financial-institution-branch-search';
import { FinancialInstitutionBranchSearchDispatch } from '@state/algolia-search/financial-institution-branch-search/financial-institution-branch-search.dispatch';
import { Action } from '@state/utils/models';
import { take } from 'rxjs/operators';
import { DropdownResult } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { ICON } from '@core/util/icon';
import {
	CreateInstitutionBranchComponent,
	CreateOrUpdateInstitutionModel,
} from '../../../../private/sale/encumbrances/encumbrance/components/institution/create-branch/create-branch.component';
import { SnackbarService } from '@services/ui/snackbar.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export enum DropdownSelect {
	CREATEINSTITUTION = 'Create new',
	SELECTINSTITUTION = 'Select Institution',
}

export interface OnSelectBranch {
	branch: Branch;
	action: Action;
}
@UntilDestroy()
@Component({
	selector: 'app-financial-institution-branch-search',
	templateUrl: './branch-search.component.html',
	standalone: false,
})
export class FinancialInstitutionBranchSearchComponent implements OnInit {
	public loading = false;
	public ICON = ICON;
	public financialInstitutionName = '';
	public dropdownSelect = DropdownSelect;
	public mortgageTypes = MortgageeType;
	public noResultsMessage = 'No branch found';

	@ViewSelectSnapshot(FinancialInstitutionBranchSearchState.getList)
	financialInstitutionsBranches;

	@Input() financialInstitution: FinancialInstitutionLender;
	@Input() fileType: MatterFileType;
	@Input() mortgageeType: MortgageeType = MortgageeType.INSTITUTION;
	@Input() autoComplete = false;
	@Input() showDischargeFields: boolean;

	@Output() onSelectedBranch = new EventEmitter<OnSelectBranch>();
	@Output() toggleAddBranch = new EventEmitter<void>();

	ngOnInit(): void {
		if (this.mortgageeType === this.mortgageTypes.COMPUTERSHARE) {
			this.noResultsMessage = 'No lender found';
		}
	}

	constructor(
		private institutionDispatch: FinancialInstitutionBranchSearchDispatch,
		private branchRepo: InstitutionBranchRepo,
		private matDialog: MatDialog,
		private snackbar: SnackbarService,
	) {}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	search = (ev: any): void => {
		this.institutionDispatch.Search(
			ev,
			this.financialInstitution?.institutionNumber,
		);
	};

	onSelect = async (
		ev: DropdownResult<
			| { value: DropdownSelect.CREATEINSTITUTION }
			| AlgoliaFinancialInstitutionBranch
		>,
	): Promise<void> => {
		if (ev.groupType === DropdownSelect.SELECTINSTITUTION) {
			const _branchDoc = await this.branchRepo
				.Get((ev.result as AlgoliaFinancialInstitutionBranch)?.id, {
					financialInstitutionId: (
						ev.result as AlgoliaFinancialInstitutionBranch
					)?.institutionId,
				})
				.pipe(take(1), untilDestroyed(this))
				.toPromise();
			this.onSelectedBranch.emit({
				branch: { ...(_branchDoc.data() as Branch), id: _branchDoc.id },
				action: Action.SET,
			});
		} else if (ev.groupType === DropdownSelect.CREATEINSTITUTION) {
			const data: CreateOrUpdateInstitutionModel = {
				showDischargeFields: this.showDischargeFields,
				institutionId: this.financialInstitution?.institutionNumber,
				fileType: this.fileType,
				branchLabel:
					this.mortgageeType === this.mortgageTypes.COMPUTERSHARE
						? 'Lender'
						: 'Branch',
				showTransitNumber:
					this.mortgageeType === this.mortgageTypes.INSTITUTION,
				mortgageeType: this.mortgageeType,
				branch: {
					name: this.financialInstitutionName,
				} as Branch,
				create: true,
			};
			// show create branch/lender
			const dialog = this.matDialog.open(CreateInstitutionBranchComponent, {
				data,
				width: '80vw',
				maxWidth: '800px',
			});
			dialog
				.afterClosed()
				.pipe(take(1), untilDestroyed(this))
				.subscribe((result) => {
					if (result) {
						this.onSelectedBranch.emit({
							branch: result as Branch,
							action: Action.CREATE,
						});
						this.snackbar.openSnackBar({
							text: 'Lender details submitted for review.',
							type: 'success',
						});
					}
				});
		}
	};

	addBranch(): void {
		this.autoComplete = true;
	}

	closeSearch(): void {
		this.financialInstitutionName = '';
		this.autoComplete = false;
		this.toggleAddBranch.emit();
	}
}
